import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import { fmtTime } from "@/lib/format";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   Support chat — shared thread pieces for the three surfaces:
   the public widget, the portal help panel and the HQ desk.
   · Bubbles are grouped by author; the customer sits right in ink,
     the assistant left on fill, a person from Listed left on white with a
     lavender dot, system notes are quiet centred lines.
   · Only transform/opacity animate.
--------------------------------------------------------------------------- */

/** Group consecutive messages by role (and author for agents). */
export function groupMessages(messages = []) {
  const groups = [];
  for (const m of messages) {
    const last = groups[groups.length - 1];
    const key = m.role === "agent" ? `agent:${m.author?.id || ""}` : m.role;
    if (last && last.key === key && m.role !== "system" && new Date(m.created_at) - new Date(last.items[last.items.length - 1].created_at) < 5 * 60 * 1000) last.items.push(m);
    else groups.push({ key, role: m.role, author: m.author, items: [m] });
  }
  return groups;
}

export function AuthorLabel({ role, author, customerLabel, className }) {
  if (role === "customer") return customerLabel ? <p className={cn("text-[11.5px] text-quiet text-right mb-1", className)}>{customerLabel}</p> : null;
  if (role === "assistant") return <p className={cn("flex items-center gap-1.5 text-[11.5px] text-quiet mb-1", className)}><span className="h-1.5 w-1.5 rounded-full bg-ink/30" />{t("sup.assistant")}</p>;
  if (role === "agent") return <p className={cn("flex items-center gap-1.5 text-[11.5px] text-quiet mb-1", className)}><span className="h-1.5 w-1.5 rounded-full bg-lavender" />{author?.name || "Listed"} · Listed</p>;
  return null;
}

/** One group of bubbles. `mine` flips the customer side (HQ reads the customer on the left). */
export function MessageGroup({ group, mine, showTime = true, customerLabel, testIdPrefix = "support-message" }) {
  const { role, items, author } = group;
  if (role === "system") {
    return (
      <div className="flex items-center gap-3 py-1" data-testid={`${testIdPrefix}-system`}>
        <span className="h-px flex-1 bg-hairline" />
        <p className="text-[11.5px] text-quiet text-center max-w-[80%] leading-[1.45]">{items.map((m) => m.text).join(" · ")}</p>
        <span className="h-px flex-1 bg-hairline" />
      </div>
    );
  }
  const right = mine;
  return (
    <div className={cn("flex flex-col", right ? "items-end" : "items-start")} data-testid={`${testIdPrefix}-${role}`} data-role={role}>
      <AuthorLabel role={role} author={author} customerLabel={customerLabel} />
      <div className={cn("flex flex-col gap-1 max-w-[86%]", right ? "items-end" : "items-start")}>
        {items.map((m, i) => (
          <div
            key={m.id}
            className={cn(
              "px-3.5 py-2.5 text-[14px] leading-[1.5] whitespace-pre-wrap break-words",
              right ? "bg-ink text-white rounded-[18px]" : role === "agent" ? "bg-surface text-ink border border-hairline rounded-[18px]" : "bg-fill text-ink rounded-[18px]",
              right ? (i === items.length - 1 ? "rounded-br-[6px]" : "") : i === items.length - 1 ? "rounded-bl-[6px]" : "",
            )}
            data-testid={`${testIdPrefix}-bubble`}
          >
            {m.text}
          </div>
        ))}
      </div>
      {showTime && <p className={cn("mt-1 text-[11px] text-quiet2 tabular-nums", right ? "text-right" : "")}>{fmtTime(items[items.length - 1].created_at)}</p>}
    </div>
  );
}

/** Three quiet dots while the assistant writes. */
export function TypingIndicator({ label = t("sup.typing"), className }) {
  return (
    <div className={cn("flex flex-col items-start", className)} data-testid="support-typing" aria-live="polite" aria-label={label}>
      <AuthorLabel role="assistant" />
      <div className="inline-flex items-center gap-1 rounded-[18px] rounded-bl-[6px] bg-fill px-3.5 h-9">
        {[0, 1, 2].map((i) => <span key={i} className="typing-dot h-1.5 w-1.5 rounded-full bg-ink/50" style={{ animationDelay: `${i * 140}ms` }} />)}
      </div>
    </div>
  );
}

/** Scrolling thread. Sticks to the bottom while the user is at the bottom. */
export function Thread({ messages, mine = "customer", typing = false, children, footer, className, customerLabel, emptyState, testId = "support-thread" }) {
  const ref = useRef(null);
  const stick = useRef(true);
  const groups = groupMessages(messages);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !stick.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, typing, footer]);
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };
  return (
    <div ref={ref} onScroll={onScroll} className={cn("flex-1 min-h-0 overflow-y-auto pm-scroll px-4 py-4 space-y-4", className)} data-testid={testId} role="log" aria-live="polite">
      {children}
      {groups.length === 0 && !typing && emptyState}
      {groups.map((g, i) => <MessageGroup key={`${g.key}-${g.items[0].id}-${i}`} group={g} mine={g.role === mine} customerLabel={g.role === "customer" ? customerLabel : undefined} showTime={i === groups.length - 1 || groups[i + 1]?.role !== g.role} />)}
      {typing && <TypingIndicator />}
      {footer}
    </div>
  );
}

/** Composer — auto-growing textarea, Enter sends, Shift+Enter breaks a line. */
export function Composer({ onSend, disabled, placeholder, busy, autoFocus, testId = "support-composer", className, maxRows = 5, hint }) {
  const [value, setValue] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    const line = 22;
    el.style.height = `${Math.min(el.scrollHeight, line * maxRows + 20)}px`;
  }, [value, maxRows]);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  const send = () => {
    const text = value.trim();
    if (!text || disabled || busy) return;
    onSend(text);
    setValue("");
    requestAnimationFrame(() => ref.current?.focus());
  };
  return (
    <div className={cn("shrink-0 border-t border-hairline bg-surface px-3 pt-2.5 pb-3", className)} data-testid={testId}>
      <div className={cn("flex items-end gap-2 rounded-[16px] border border-ink/[0.12] bg-surface pl-3.5 pr-1.5 py-1.5 transition-[border-color,box-shadow] duration-150 focus-within:border-[var(--focus-border)] focus-within:shadow-ring", disabled && "opacity-60")}>
        <textarea
          ref={ref}
          rows={1}
          value={value}
          disabled={disabled}
          placeholder={placeholder || t("sup.placeholder")}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
          aria-label={placeholder || t("sup.placeholder")}
          className="flex-1 min-w-0 resize-none bg-transparent py-1.5 text-[14px] leading-[22px] text-ink placeholder:text-ink/40 focus:outline-none max-h-[140px]"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={send}
          disabled={disabled || busy || !value.trim()}
          aria-label={t("common.send")}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-white transition-[transform,opacity,background-color] duration-150 ease-out hover:bg-[#2A2620] active:scale-95 disabled:opacity-30 disabled:pointer-events-none focus-visible:outline-none focus-visible:shadow-ring"
          data-testid={`${testId}-send`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" strokeWidth={2.2} />}
        </button>
      </div>
      {hint && <p className="mt-2 px-1 text-[11px] text-quiet2 leading-[1.4]">{hint} <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-ink transition-colors" data-testid={`${testId}-privacy-link`}>{t("legal.privacy_short")}</a></p>}
    </div>
  );
}

/** Status line for a case, in customer words. */
export function caseStatusLine(c) {
  if (!c) return "";
  const hasPerson = c.status === "waiting_on_hq" || c.status === "waiting_on_customer" || (c.assignee && !c.ai_handled);
  if (c.status === "resolved") return t("sup.status.resolved");
  if (c.status === "closed") return t("sup.status.closed");
  if (c.status === "waiting_on_customer") return t("sup.status.waiting_on_customer", { name: c.assignee?.name || "Listed" });
  if (hasPerson) return t("sup.status.waiting_on_hq");
  return t("sup.status.open");
}
